import React from 'react';
import { Download, FileText, CheckCircle } from 'lucide-react';

const GeneratedEssay = ({ templateData, generatePDF }) => {
  const isComplete = Object.values(templateData).every(value => value.trim() !== '');
  const completionCount = Object.values(templateData).filter(value => value.trim() !== '').length;
  
  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div 
        className="relative rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(147, 51, 234, 0.9), rgba(219, 39, 119, 0.9)), url(https://images.unsplash.com/photo-1471107340929-a87cd0f5b5f3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1973&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <h2 className="text-3xl font-bold mb-3">Generated Essay Preview</h2>
          <p className="text-purple-100 text-lg">
            Review your letter or email and download as PDF when ready
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Your Document</h3>
              <p className="text-gray-600">
                {isComplete ? (
                  <span className="flex items-center space-x-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Complete and ready to download</span>
                  </span>
                ) : (
                  <span>{completionCount}/8 fields completed</span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={generatePDF}
            disabled={!isComplete}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
              isComplete 
                ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Download className="w-5 h-5" />
            <span>Download PDF</span>
          </button>
        </div>

        {/* Document Preview */}
        <div className="p-8">
          <div className="max-w-4xl mx-auto">
            <div 
              className="bg-white border-2 border-gray-200 rounded-lg p-8 shadow-inner min-h-96"
              style={{ 
                backgroundImage: 'linear-gradient(90deg, transparent 74px, #e5e7eb 74px, #e5e7eb 76px, transparent 76px)',
                backgroundSize: '100% 24px'
              }}
            >
              {/* Document Header */}
              <div className="mb-8 pb-4 border-b-2 border-gray-300">
                <h1 className="text-xl font-bold text-center text-gray-800 mb-4">
                  {templateData.type.toUpperCase().replace('-', ' ')} LETTER/EMAIL
                </h1>
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                  <div><strong>Date:</strong> {templateData.date || '[Date]'}</div>
                  <div><strong>Subject:</strong> {templateData.subject || '[Subject]'}</div>
                  <div><strong>To:</strong> {templateData.recipient || '[Recipient]'}</div>
                  <div><strong>From:</strong> {templateData.sender || '[Sender]'}</div>
                </div>
              </div>

              {/* Content Sections */}
              <div className="space-y-6 text-gray-800 leading-relaxed">
                <div className="space-y-1">
                  <div className="text-blue-600 font-medium text-lg">
                    {templateData.greeting || '[Greeting]'}
                  </div>
                </div>

                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-green-600 uppercase tracking-wide">Opening</h4>
                  <p className="text-gray-800">
                    {templateData.opening || '[Opening paragraph - explain your reason for writing]'}
                  </p>
                </div>

                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Main Body</h4>
                  <div className="text-gray-800 whitespace-pre-line">
                    {templateData.mainBody || '[Main body - present your details in cohesive, coherent paragraphs]'}
                  </div>
                </div>

                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-orange-600 uppercase tracking-wide">Conclusion</h4>
                  <p className="text-gray-800">
                    {templateData.conclusion || '[Concluding paragraph - polite final message and closing remark]'}
                  </p>
                </div>

                <div className="space-y-1 mt-8">
                  <div className="text-red-600 font-medium whitespace-pre-line">
                    {templateData.closing || '[Complimentary close - should match salutation]'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Tips */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-xl">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                Writing Tips
              </h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Ensure your tone matches your audience and purpose</li>
                <li>• Use formal language for business letters, informal for friends</li>
                <li>• Keep paragraphs focused on one main idea</li>
                <li>• Proofread for grammar, spelling, and punctuation</li>
                <li>• Make sure your closing matches your opening salutation</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Format Guide</h4>
              <div className="text-sm text-gray-700 space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span>Greeting/Salutation</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span>Opening - Reason for writing</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-purple-500 rounded"></div>
                  <span>Main Body - Key content</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-orange-500 rounded"></div>
                  <span>Conclusion - Final thoughts</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded"></div>
                  <span>Closing - Sign off</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratedEssay;
